import { useState, useEffect } from 'react'
import { GameEngine, PlayerStats } from '../game/GameEngine'
import { ethers } from 'ethers'

interface PlayerProgression {
  playerAddress: string
  totalExperience: number
  stats: PlayerStats
  lastUpdated: number
}

// AGC: PlayerXP contract ABI (only functions we need)
// AGC: FIX 2025-11-28 - Corrected ABI to match actual contract (experienceToNextLevel instead of lastBattleTimestamp)
const PLAYER_XP_ABI = [
  'function players(address) external view returns (uint256 totalExperience, uint8 level, uint256 experienceToNextLevel, uint32 totalBattles, uint32 wins, uint32 losses, uint32 draws)',
  'function addExperience(address player, uint256 xpGain) external',
  'function recordBattle(address player, string memory outcome, uint256 xpGained) external',
  'function calculateLevel(uint256 totalXP) external pure returns (uint8)',
  'function getPlayerData(address player) external view returns (uint256 totalExperience, uint8 level, uint256 experienceToNextLevel, uint32 totalBattles, uint32 wins, uint32 losses, uint32 draws)'
]

export const usePlayerProgression = (playerAddress: string) => {
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [useBlockchain, setUseBlockchain] = useState(true) // AGC: Toggle for blockchain vs localStorage

  // AGC: Get PlayerXP contract address from environment
  const getPlayerXPContract = async () => {
    const contractAddress = import.meta.env.VITE_PLAYER_XP_ADDRESS
    
    if (!contractAddress || !window.ethereum) {
      console.warn('⚠️ PlayerXP contract not configured or no wallet, using localStorage fallback')
      setUseBlockchain(false)
      return null
    }

    try {
      // AGC: Initialize provider using connected Farcaster wallet context
      const provider = new ethers.BrowserProvider(window.ethereum)

      // AGC: Safely detect current chain; on preview platforms some RPC methods are blocked
      let chainId: number | null = null
      try {
        const network = await provider.getNetwork()
        chainId = Number(network.chainId)
      } catch (networkError) {
        console.warn(
          '⚠️ AGC: Unable to read active chain from provider – preview is blocking eth_chainId. Using local progression cache instead.',
          networkError
        )
        return null
      }

      // AGC: Player XP contract exists only on Base Sepolia (84532)
      if (chainId !== 84532) {
        console.warn(
          `⚠️ AGC: PlayerXP contract only deployed on Base Sepolia (84532) - current chain ${chainId}. Falling back to local progression cache.`
        )
        return null
      }

      return new ethers.Contract(contractAddress, PLAYER_XP_ABI, provider)
    } catch (error) {
      console.error('Failed to initialize PlayerXP contract:', error)
      setUseBlockchain(false)
      return null
    }
  }

  // AGC: FIX 2025-11-28 - Read directly from blockchain using RPC (source of truth)
  const loadDirectFromBlockchain = async (): Promise<PlayerProgression | null> => {
    const PLAYER_XP_ADDRESS = import.meta.env.VITE_PLAYER_XP_ADDRESS
    const RPC_URL = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
    
    if (!PLAYER_XP_ADDRESS) {
      console.warn('⚠️ VITE_PLAYER_XP_ADDRESS not set')
      return null
    }
    
    try {
      const provider = new ethers.JsonRpcProvider(RPC_URL)
      const contract = new ethers.Contract(PLAYER_XP_ADDRESS, PLAYER_XP_ABI, provider)
      
      console.log(`🔗 Reading player XP from blockchain for ${playerAddress.slice(0, 10)}...`)
      const playerData = await contract.getPlayerData(playerAddress)
      const totalExperience = Number(playerData[0])
      const level = Number(playerData[1])
      
      console.log(`✅ Blockchain data (direct): Level ${level}, XP ${totalExperience}`)
      
      const stats = GameEngine.calculatePlayerStats(totalExperience)
      
      return {
        playerAddress,
        totalExperience,
        stats,
        lastUpdated: Date.now()
      }
    } catch (error: any) {
      console.warn(`⚠️ Direct blockchain query failed: ${error.message}`)
      return null
    }
  }

  // AGC: Load player progression from blockchain (via backend API with Firebase caching)
  // AGC: FIX 2025-11-28 - Now reads DIRECTLY from blockchain first, backend as fallback
  const loadPlayerProgressionFromChain = async (): Promise<PlayerProgression | null> => {
    try {
      console.log(`🔗 Loading player data for ${playerAddress.slice(0, 10)}...`)

      // AGC: FIX 2025-11-28 - Read directly from blockchain FIRST (source of truth)
      const directData = await loadDirectFromBlockchain()
      if (directData) {
        return directData
      }

      // AGC: Fallback to backend API with Firebase caching
      console.log(`📊 Trying backend API fallback...`)
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
      const response = await fetch(`${backendUrl}/api/player-stats/${playerAddress}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        // AGC: 404 is normal for new players - try BrowserProvider as last resort
        if (response.status === 404) {
          console.log(`📊 New player detected (no cached stats)`)
        } else {
          console.warn(`⚠️ API failed (${response.status})`)
        }
        
        // AGC: Last resort - try BrowserProvider (requires wallet connection)
        const contract = await getPlayerXPContract()
        if (!contract) return null

        const playerData = await contract.getPlayerData(playerAddress)
        const totalExperience = Number(playerData[0])
        const level = Number(playerData[1])
        
        console.log(`✅ Blockchain data (BrowserProvider fallback): Level ${level}, XP ${totalExperience}`)

        const stats = GameEngine.calculatePlayerStats(totalExperience)

        return {
          playerAddress,
          totalExperience,
          stats,
          lastUpdated: Date.now()
        }
      }

      const data = await response.json()
      const totalExperience = data.totalExperience || 0
      const level = data.level || 1
      
      console.log(`✅ Blockchain data (cached): Level ${level}, XP ${totalExperience}`)

      // AGC: Calculate stats from total XP
      const stats = GameEngine.calculatePlayerStats(totalExperience)

      return {
        playerAddress,
        totalExperience,
        stats,
        lastUpdated: Date.now()
      }
    } catch (error) {
      console.error('Error loading from blockchain:', error)
      return null
    }
  }

  // AGC: Load player progression from localStorage (fallback)
  const loadPlayerProgressionFromStorage = (): PlayerProgression => {
    const storageKey = `origami_player_${playerAddress}`
    const stored = localStorage.getItem(storageKey)
    
    if (stored) {
      try {
        const data = JSON.parse(stored) as PlayerProgression
        console.log(`📦 Loaded from localStorage: Level ${data.stats.level}, XP ${data.totalExperience}`)
        return data
      } catch (error) {
        console.error('Failed to parse stored player progression:', error)
      }
    }
    
    // AGC: Default progression for new players
    console.log('🆕 Creating new player progression')
    return {
      playerAddress,
      totalExperience: 0,
      stats: GameEngine.calculatePlayerStats(0),
      lastUpdated: Date.now()
    }
  }

  // AGC: Save player progression to localStorage (backup only)
  const savePlayerProgressionToStorage = (progression: PlayerProgression) => {
    const storageKey = `origami_player_${playerAddress}`
    try {
      localStorage.setItem(storageKey, JSON.stringify(progression))
    } catch (error) {
      console.error('Failed to save player progression:', error)
    }
  }

  // AGC: Add experience and handle level ups (ON-CHAIN)
  const addExperience = async (xpGain: number): Promise<{ leveledUp: boolean; oldLevel: number; newLevel: number }> => {
    if (!useBlockchain) {
      // AGC: Fallback to localStorage
      return addExperienceOffChain(xpGain)
    }

    try {
      const contract = await getPlayerXPContract()
      if (!contract) return addExperienceOffChain(xpGain)

      // AGC: Get current level before awarding XP
      const currentData = await contract.getPlayerData(playerAddress)
      const oldLevel = Number(currentData.level) || 1

      console.log(`🔗 Awarding ${xpGain} XP on-chain to ${playerAddress.slice(0, 10)}...`)

      // AGC: Need signer to call contract
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const contractWithSigner = contract.connect(signer)

      // AGC: Call addExperience on contract
      console.log(`📝 Calling addExperience(${playerAddress}, ${xpGain})...`)
      const tx = await contractWithSigner.addExperience(playerAddress, xpGain)
      console.log(`⏳ Transaction sent: ${tx.hash}`)
      console.log(`🔗 View on BaseScan: https://sepolia.basescan.org/tx/${tx.hash}`)
      
      const receipt = await tx.wait()
      console.log(`✅ XP awarded on-chain! Gas used: ${receipt.gasUsed}`)

      // AGC: Get new data after XP award
      const newData = await contract.getPlayerData(playerAddress)
      const newLevel = Number(newData.level)
      const totalXP = Number(newData.totalExperience)

      console.log(`📊 New player data: Level ${newLevel}, Total XP ${totalXP}`)

      // AGC: Update local state
      const newStats = GameEngine.calculatePlayerStats(totalXP)
      setPlayerStats(newStats)

      // AGC: Also save to localStorage as backup
      savePlayerProgressionToStorage({
        playerAddress,
        totalExperience: totalXP,
        stats: newStats,
        lastUpdated: Date.now()
      })

      const leveledUp = newLevel > oldLevel

      if (leveledUp) {
        console.log(`🎉 Level up! ${oldLevel} → ${newLevel}`)
      }

      return {
        leveledUp,
        oldLevel,
        newLevel
      }
    } catch (error: any) {
      console.error('❌ Error awarding XP on-chain:')
      console.error(`  Message: ${error.message}`)
      console.error(`  Code: ${error.code}`)
      if (error.data) {
        console.error(`  Data: ${error.data}`)
      }
      if (error.reason) {
        console.error(`  Reason: ${error.reason}`)
      }
      console.error('  Falling back to localStorage...')
      return addExperienceOffChain(xpGain)
    }
  }

  // AGC: Add experience off-chain (fallback)
  const addExperienceOffChain = (xpGain: number): { leveledUp: boolean; oldLevel: number; newLevel: number } => {
    const progression = loadPlayerProgressionFromStorage()
    const oldLevel = progression.stats.level
    const newTotalXP = progression.totalExperience + xpGain
    const newStats = GameEngine.calculatePlayerStats(newTotalXP)
    
    const updatedProgression: PlayerProgression = {
      ...progression,
      totalExperience: newTotalXP,
      stats: newStats,
      lastUpdated: Date.now()
    }
    
    savePlayerProgressionToStorage(updatedProgression)
    setPlayerStats(newStats)
    
    const leveledUp = newStats.level > oldLevel
    
    if (leveledUp) {
      console.log(`🎉 Level up! ${oldLevel} → ${newStats.level}`)
    }
    
    return {
      leveledUp,
      oldLevel,
      newLevel: newStats.level
    }
  }

  // AGC: Get random opponent level (slightly varied around player level)
  const getOpponentLevel = (): number => {
    if (!playerStats) return 1
    
    const playerLevel = playerStats.level
    const minLevel = Math.max(1, playerLevel - 2)
    const maxLevel = Math.min(100, playerLevel + 3)
    
    return Math.floor(Math.random() * (maxLevel - minLevel + 1)) + minLevel
  }

  // AGC: Calculate experience gain for battle outcome
  const calculateBattleXP = (outcome: 'WIN' | 'DRAW' | 'LOSE', opponentLevel: number, battleType: 'PVE' | 'PVP' = 'PVP'): number => {
    if (!playerStats) return 0
    console.log(`🧮 Calculating XP: Outcome=${outcome}, OpponentLvl=${opponentLevel}, PlayerLvl=${playerStats.level}, Type=${battleType}`)
    const xp = GameEngine.calculateExperienceGain(outcome, opponentLevel, playerStats.level, battleType)
    console.log(`💡 XP Result: ${xp}`)
    return xp
  }

  // AGC: Reset player progression (for testing)
  const resetProgression = () => {
    const storageKey = `origami_player_${playerAddress}`
    localStorage.removeItem(storageKey)
    const newProgression = loadPlayerProgressionFromStorage()
    setPlayerStats(newProgression.stats)
  }

  // AGC: Add experience directly (for testing)
  const addTestExperience = (amount: number) => {
    return addExperience(amount)
  }

  // AGC: Load progression on mount (TRY BLOCKCHAIN FIRST)
  useEffect(() => {
    if (playerAddress) {
      setLoading(true)
      
      loadPlayerProgressionFromChain()
        .then((blockchainData) => {
          if (blockchainData) {
            // AGC: Successfully loaded from blockchain
            setPlayerStats(blockchainData.stats)
            // AGC: Also save to localStorage as backup
            savePlayerProgressionToStorage(blockchainData)
          } else {
            // AGC: Fallback to localStorage
            const storageData = loadPlayerProgressionFromStorage()
            setPlayerStats(storageData.stats)
          }
        })
        .catch((error) => {
          console.error('Error loading progression:', error)
          // AGC: Fallback to localStorage
          const storageData = loadPlayerProgressionFromStorage()
          setPlayerStats(storageData.stats)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [playerAddress])

  // AGC: Listen for local progression updates triggered by battle history changes
  useEffect(() => {
    if (typeof window === 'undefined' || !playerAddress) {
      return
    }

    const eventListener: EventListener = (event) => {
      const customEvent = event as CustomEvent<{ walletAddress?: string }>
      const updatedWallet = customEvent.detail?.walletAddress

      if (!updatedWallet || updatedWallet.toLowerCase() !== playerAddress.toLowerCase()) {
        return
      }

      ;(async () => {
        const chainData = await loadPlayerProgressionFromChain()
        if (chainData) {
          setPlayerStats(chainData.stats)
          savePlayerProgressionToStorage(chainData)
        } else {
          const storageData = loadPlayerProgressionFromStorage()
          setPlayerStats(storageData.stats)
        }
      })()
    }

    window.addEventListener('origami:player-progression-updated', eventListener)

    return () => {
      window.removeEventListener('origami:player-progression-updated', eventListener)
    }
  }, [playerAddress])

  return {
    playerStats,
    loading,
    addExperience,
    getOpponentLevel,
    calculateBattleXP,
    resetProgression,
    addTestExperience,
    useBlockchain // AGC: Expose whether using blockchain or not
  }
}
